// fittingSessionStore.js
//
// WHY THIS EXISTS
// ────────────────────────────────────────────────────────────────────────
// `handleSubmit` in DataFittingPage.jsx kicks off a single (non-polling)
// axios request and awaits it in place. As long as that request's promise
// chain is referenced by something, the JS engine keeps the closure alive
// and its `.then` / `.catch` / `finally` callbacks *will* run to
// completion — navigating to another route does NOT cancel the underlying
// network request. The problem was purely that those callbacks called
// React state setters (`setLoading`, `setResult`, `setPopup`, ...) that
// belonged to a DataFittingPage instance which react-router had already
// unmounted, so the update was silently dropped: the run kept going on the
// server, but the UI had no way to show that, and returning to
// /data-fitting always rendered a fresh, idle page.
//
// Fix: move the run's lifecycle state OUT of the component and into this
// plain module-level object. Modules aren't part of the component tree, so
// they aren't touched by mount/unmount at all — only a full browser reload
// clears this. DataFittingPage subscribes to it via `useFittingSession` and
// renders whatever it currently holds, whether that's "idle", "a run in
// progress that was started 40s ago on a page you've since navigated away
// from", or "a result that finished while you were on /contact".
//
// This intentionally only tracks the RUN's lifecycle, not the input form
// fields — the brief for this feature is "don't stop the running fit and
// show it's still running when the user comes back", not "preserve every
// unsubmitted keystroke across navigation". It works identically whether
// the run was started from "Initialise with Example" or a custom reaction
// system — this store has no idea which one it is, it just holds whatever
// handleSubmit gives it.

let state = {
  loading: false,
  result: null,
  popup: { show: false, message: '', type: '' },
  runStartedAt: null,
  estimatedRuntimeSec: 0,
  abortController: null,
  // Snapshot of what's actually running, so the Input panel can show a
  // meaningful "still running" banner immediately on remount, before any
  // other state has re-hydrated. Purely descriptive — never drives logic.
  runMeta: null, // { reactionSystem, fitScope, label }
};

const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try { fn(state); } catch (e) { /* one bad listener shouldn't break the rest */ }
  });
}

export function getFittingSessionState() {
  return state;
}

export function setFittingSessionState(patch) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  notify();
  return state;
}

export function subscribeFittingSession(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Clears everything back to idle — used by the Input panel's Reset action.
// Normal completion/cancellation already clears `loading` via handleSubmit's
// own `finally` block; this is for a full hard reset of the session.
export function resetFittingSession() {
  state = {
    loading: false,
    result: null,
    popup: { show: false, message: '', type: '' },
    runStartedAt: null,
    estimatedRuntimeSec: 0,
    abortController: null,
    runMeta: null,
  };
  notify();
}